// ForgotPassword.jsx - Password reset request component for VibrantCast
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabaseClient';
import { AlertCircle, ArrowLeft, Mail, Check } from 'lucide-react';
import '../styles/Auth.css';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  const navigate = useNavigate();

  // Check if user is already logged in
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        navigate('/curator/dashboard', { replace: true });
      }
    };
    checkAuth();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) throw error;

      setSuccess(true);
    } catch (error) {
      console.error('Password reset error:', error);
      setError(error.message || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-header">
            <div className="success-icon-large">
              <Mail size={48} />
            </div>
            <h2>Check Your Email</h2>
            <p className="auth-subtitle">
              We've sent a password reset link to <strong>{email}</strong>
            </p>
          </div>

          <div className="auth-info-box">
            <p>Click the link in the email to reset your password. If you don't see the email, check your spam folder.</p>
          </div>

          <div className="auth-actions">
            <button
              onClick={() => {
                setSuccess(false);
                setEmail('');
              }}
              className="auth-button-secondary"
            >
              Try Different Email
            </button>
            <Link to="/login" className="auth-button">
              Back to Login
            </Link>
          </div>

          <div className="auth-footer">
            <p className="resend-text">
              Didn't receive the email?{' '}
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="resend-link"
              >
                {loading ? 'Sending...' : 'Resend'}
              </button>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-logo-container">
            <img 
              src="https://res.cloudinary.com/dh9jfogh1/image/upload/t_fitmax-300/v1729693346/vibrant-logo_ds6pwg.png" 
              alt="VibrantCast" 
              className="auth-logo-inline"
            />
          </div>
          <h2>Reset Password</h2>
          <p className="auth-subtitle">
            Enter your email and we'll send you a link to reset your password
          </p>
        </div>

        {error && (
          <div className="auth-error">
            <AlertCircle size={20} />
            <div>
              <div className="error-title">Error</div>
              <p className="error-message">{error}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <div className="input-container">
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                disabled={loading}
                autoFocus
              />
            </div>
          </div>

          <button
            type="submit"
            className="auth-button"
            disabled={loading || !email}
          >
            {loading ? "Sending..." : "Send Reset Link"}
          </button>
        </form>

        <div className="auth-footer">
          <Link to="/login" className="back-link">
            <ArrowLeft size={16} />
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
